import { ListBaseModel } from '@/api/models';
import { AttachmentsModel } from '@/features/attachment/_models/attachment';
import { TagTargetBaseModel } from '@/features/tag/models';
import { HierarchyLabelModel } from '@/features/tenant-manager/models';
import { t } from '@/i18n';
import {
  BackendBaseModel,
  extendEnumWithPrefix,
  paginationSearchParamSchema,
} from '@/utils';
import { z } from 'zod';
import { PinpointModel } from './pinpoint';
import { RepliesModel } from './reply';

/// ////////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////////

export const commentSortType = z.enum(['-replies_updated_at', '-created_at']);
export const CommentSortTypeLabelModifier = {
  '-replies_updated_at': t('latest-reply'),
  '-created_at': t('created-datetime'),
} as const satisfies Record<z.infer<typeof commentSortType>, string>;

export const commentOrderingType = extendEnumWithPrefix(
  z.enum([...commentSortType.options, 'last_modified_at']),
  '-',
);
export type CommentOrderingType = z.infer<typeof commentOrderingType>;

export const commentKeyType = z.enum(['project']);
const _CommentKeyLabelModifier = {
  project: t('project'),
} as const satisfies Record<z.infer<typeof commentKeyType>, string>;

export const commentPublishType = z.enum(['open', 'strict', 'private']);
export const CommentPublishTypeLabelModifier = {
  open: t('all'),
  strict: t('publish-type-strict'),
  private: t('publish-type-private'),
} as const satisfies Record<z.infer<typeof commentPublishType>, string>;

export const commentReadStatusEnum = z.enum(['all', 'unread', 'read']);
const _CommentReadStatusLabelModifier = {
  all: t('all'),
  read: t('comment.read'),
  unread: t('comment.unread'),
} as const satisfies Record<z.infer<typeof commentReadStatusEnum>, string>;

export const commentReadStatusType = z.enum(['read', 'unread', 'unknown']);

export const commentBulkUpdateStatusActionType = z.enum([
  'read',
  'unread',
  'read-and-resolved',
  'unresolved',
]);

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

export const CommentModel = BackendBaseModel.extend({
  lastModifiedAt: z.string(),
  content: z.string().min(1),
  resolved: z.boolean(),
  publishType: commentPublishType,
  pinpoint: PinpointModel.nullish(),
  pinpointKey: z.string().nullish(),
  key: commentKeyType,
  objectId: z.string().uuid(),
  objectName: z.string().nullable(),
  objectAdditionalAttrs: z
    .object({
      project: z
        .object({
          fiscalYear: z.number().nullish(),
          organizationName: z.string().nullish(),
        })
        .nullish(),
    })
    .nullish(),
  labelId: z.string().uuid().nullish(),
  label: HierarchyLabelModel.nullable(),
  authorId: z.string().uuid(),
  attachmentIds: z.string().uuid().array(),
  attachments: AttachmentsModel.optional(),
  assigneeIds: z.string().uuid().array(),
  relatedUserIds: z.string().uuid().array(),
  sharedOrganizationIds: z.string().uuid().array(),
  readStatusChangedAt: z.string().nullish(),
  replies: RepliesModel.nullish(),
  tagTargets: TagTargetBaseModel.array(),
  archiveCommentId: z.string().nullable(),
  readStatus: commentReadStatusType.transform((value) => {
    if (value === 'unknown') {
      return 'read';
    }
    return value;
  }),
});

export const CommentListElementModel = CommentModel.omit({
  replies: true,
});

export const CommentListModel = ListBaseModel.extend({
  results: CommentListElementModel.array(),
});

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

export const CommentListParamsSchema = CommentModel.pick({
  labelId: true,
})
  .extend({
    all: z.boolean(),
    createdAtFrom: z.string().nullable(),
    createdAtTo: z.string().nullable(),
    key: commentKeyType.optional(),
    keyword: z.string().nullable(),
    objectId: z.string().uuid(),
    ordering: z.string(),
    resolved: z.boolean().nullable(),
    /**リクエスト時にはgeneratePinpointKeyで生成したpinpointKeyを指定する */
    pinpointKey: z.string().nullable(),
    publishType: commentPublishType.nullish(),
    readStatus: commentReadStatusEnum.nullish(),
    tagIds: z.string().uuid().array().nullish(),
    authorId: CommentModel.shape.authorId.nullish(),
    assigneeId: z.string().uuid().nullish(),
    cascader: z.string().uuid().nullish(),
    fiscalYear: z.number().nullish(),
  })
  .merge(paginationSearchParamSchema)
  .partial();

export const CommentFormSchema = CommentModel.pick({
  content: true,
  publishType: true,
  relatedUserIds: true,
  sharedOrganizationIds: true,
  attachments: true,
}).extend({
  labelId: z.string().uuid().nullish(),
  assigneeIds: CommentModel.shape.assigneeIds.min(1),
  tagIds: z.string().uuid().array().optional(),
});

export const ShareCommentFormSchema = CommentModel.pick({
  relatedUserIds: true,
  sharedOrganizationIds: true,
}).extend({
  assigneeIds: CommentModel.shape.assigneeIds.min(1),
});

export const CreateCommentParamsSchema = CommentFormSchema.merge(
  CommentModel.pick({
    key: true,
    objectId: true,
    resolved: true,
    pinpoint: true,
    pinpointKey: true,
  }),
).extend({
  attachmentIds: CommentModel.shape.attachmentIds.optional(),
});

export const CreateCommentResponseSchema = z.object({
  id: z.string().uuid(),
});

export const UpdateCommentParamsSchema = CommentFormSchema.extend({
  id: z.string().uuid(),
  attachmentIds: CommentModel.shape.attachmentIds.optional(),
});

export const ResolveCommentParamsSchema = z.object({
  resolved: z.boolean(),
});

export const ShareCommentParamsSchema = z.object({
  assigneeIds: CommentModel.shape.assigneeIds.nullable(),
  relatedUserIds: CommentModel.shape.relatedUserIds.nullable(),
  sharedOrganizationIds: CommentModel.shape.sharedOrganizationIds.nullable(),
});
